import { useRef, useState } from "react";
import { QUOTE_ATTACHMENT_LIMITS } from "../constants";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import { QuoteAttachmentIntake } from "../validation";
import { FileChips } from "./FileChips";
import { AlertIcon, PaperclipIcon } from "./icons";

const intake = new QuoteAttachmentIntake();

interface Props {
  files: File[];
  error?: string;
  onChange: (files: File[]) => void;
}

export function AttachmentFields({ files, error, onChange }: Props) {
  const copy = useQuoteCopy();
  const inputRef = useRef<HTMLInputElement>(null);
  const [feedback, setFeedback] = useState("");

  const addFiles = async (incoming: FileList | File[]) => {
    const result = await intake.add(files, Array.from(incoming), copy);
    setFeedback(result.feedback);
    if (result.changed) onChange(result.files);
  };

  return (
    <div className="nv-attach">
      <div className="nv-attach-zone">
        <p className="nv-attach-hint">
          {copy.t("services.attachHint", {
            maxFiles: QUOTE_ATTACHMENT_LIMITS.maxFiles,
            maxFileMb: QUOTE_ATTACHMENT_LIMITS.maxFileMb,
            maxTotalMb: QUOTE_ATTACHMENT_LIMITS.maxTotalMb,
          })}
        </p>
        <button
          type="button"
          id="nv-attach-btn"
          className="nv-attach-btn"
          aria-invalid={error ? true : undefined}
          aria-describedby={error || feedback ? "nv-attach-input-err" : undefined}
          onClick={() => inputRef.current?.click()}
        >
          <PaperclipIcon size={16} />
          {copy.t("services.addFiles")}
        </button>
      </div>
      <FileChips files={files} onRemove={(index) => onChange(files.filter((_, i) => i !== index))} />
      <input
        ref={inputRef}
        id="nv-attach-input"
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
        multiple
        className="nv-sr-only"
        aria-invalid={error ? true : undefined}
        aria-describedby={error || feedback ? "nv-attach-input-err" : undefined}
        onChange={(e) => {
          if (e.target.files?.length) void addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      {(error || feedback) && (
        <span className="nv-error" id="nv-attach-input-err" role={error ? "alert" : undefined}>
          <AlertIcon size={13} />
          <span>{error || feedback}</span>
        </span>
      )}
    </div>
  );
}
